import dayjs from 'dayjs';

import { EmployeeChartData } from '~hooks/query/company/useCompanyEmployeeChart';

import { AvailableYears } from './types';

type Period = 3 | 6 | 12;

/**
 * 오늘 기준으로 1년,3년,5년의 모든 월정보를 문자열 배열로 반환한다. (YYYY-MM)
 * byQuarter인 경우 분기가 종료되는 날짜의 정보만 가져온다.
 * @param year
 * @param byQuarter
 * @returns
 */
export function getXAxisDates(year: AvailableYears) {
  const length = year * 12;
  const dates = new Array(length).fill('');
  const lastYear = dayjs().subtract(year, 'year').add(1, 'month');

  dates.reduce((prev, current, index) => {
    dates[index] = prev.format('YYYY-MM-01');

    return prev.add(1, 'month');
  }, lastYear);

  return dates;
}

/**
 * 날짜를 특정년도만큼 잘라내고(1년/3년/5년) 오름차순으로 내려오는 데이터를 내림차순으로 변경
 * FIXME: 서버에 내림차순으로 내려줄 수 있는지
 *
 * @param data
 * @param year
 * @returns
 */
export function getSliceData(data: any[], year: AvailableYears) {
  if (!data) {
    return [];
  }

  return data.slice(year * -12);
}

export function getBarChartSizeProps(year: AvailableYears, hasPadding?: boolean) {
  switch (year) {
    case 1:
      if (hasPadding) {
        return {
          barThickness: 14,
          borderWidth: 2,
          borderColor: 'transparent',
        };
      }

      return {
        barThickness: 10,
      };
    case 3:
      if (hasPadding) {
        return {
          barThickness: 10,
          borderWidth: 2,
          borderColor: 'transparent',
        };
      }

      return {
        barThickness: 6,
      };
    default:
      if (hasPadding) {
        return {
          barThickness: 7,
          borderWidth: 2,
          borderColor: 'transparent',
        };
      }

      return {
        barThickness: 3,
      };
  }
}

export function getQuarter(month: number) {
  return Math.ceil(month / 3);
}

/**
 * 쿼터별로 데이터를 머지한다.
 * date는 YYYY.NQ
 * @param data
 */
export function getDataByQuarter(data: EmployeeChartData[], year: AvailableYears) {
  const periodNum = 3;
  const result: EmployeeChartData[] = [];
  const slicedData = getSliceData(data, year);

  slicedData.reduce<EmployeeChartData | null>((prev, current, index) => {
    const date = dayjs(current.date);
    const month = date.get('month') + 1;
    const quarter = getQuarter(month);

    if (prev === null) {
      // 분기/년의 첫번째 개월이자 데이터의 마지막 요소인 경우
      if (index === slicedData.length - 1) {
        result.push({
          ...current,
          date: `${date.format('YYYY')}.${quarter}Q`,
        });

        return null;
      }

      return current;
    }

    if (month % periodNum === 0 || index === slicedData.length - 1) {
      result.push({
        date: `${date.format('YYYY')}.${quarter}Q`,
        hired: prev.hired + current.hired,
        left: prev.left + current.left,
        total: current.total || prev.total,
      });

      return null;
    }

    return {
      total: current.total,
      hired: prev.hired + current.hired,
      left: prev.left + current.left,
    };
  }, null);

  return result;
}

export function getLabelsByPediod(data: EmployeeChartData[], period: Period, year: AvailableYears) {
  if (period !== 12) {
    return data.map((item) => item.date);
  }

  const periodNum = 3;
  const result: string[] = [];

  // @ts-ignore
  data.reduce<EmployeeChartData | null>((prev, current, index) => {
    const date = dayjs(current.date);
    const month = date.get('month') + 1;
    const quarter = getQuarter(month);

    if (prev === null) {
      if (index === data.length - 1) {
        result.push(`${date.format('YYYY')}.${quarter}Q`);

        return null;
      }

      return current;
    }

    if (month % periodNum === 0 || index === data.length - 1) {
      result.push(`${date.format('YYYY')}.${quarter}Q`);

      return null;
    }

    return {
      // @ts-ignore
      hired: prev.hired + current.hired,
      // @ts-ignore
      left: prev.left + current.left,
    };
  }, null);

  return getSliceData(result, year);
}

/**
 * 이번달 기준으로 1년, 3년, 5년치의 템플릿 데이터를 반환
 * @param year
 * @returns
 */
export function getDateArrayTemplate(year: AvailableYears) {
  const today = dayjs();
  const months = year * 12;
  const result: EmployeeChartData[] = [];

  for (let i = 1; i <= months; i++) {
    const date = today.subtract(months - i, 'month');
    result.push({
      date: date.format('YYYY-MM-01'),
      total: null,
      hired: null,
      left: null,
    });
  }

  return result;
}

/**
 * 실제 데이터와 template을 합병한 결과 반환
 * @param year
 * @param data
 * @returns
 */
export function getMergedDateArray(year: AvailableYears, data: Partial<EmployeeChartData>[]) {
  const template = getDateArrayTemplate(year);

  data.forEach((dateObj) => {
    const date = dateObj.date;
    const { hired, total, left } = dateObj;
    const targetIndex = template.findIndex((obj) => {
      return obj.date === date;
    });

    if (targetIndex >= 0) {
      template[targetIndex]['hired'] = hired;
      template[targetIndex]['total'] = total;
      template[targetIndex]['left'] = left;
    }
  });

  return template;
}

/**
 * 객체의 total값 유무에 따라 데이터가 유효한지 판단
 * @param data
 * @returns
 */
export function getIsEmptyData(data: EmployeeChartData[]) {
  return data.every((obj) => !obj.total);
}
